/** @fileoverview Confirms the Platform sign-in before opening a hosted app. */

import { useEffect, useState } from 'react';
import LoadingState from '../components/ui/loading-state';
import { isValidAppAddress } from '../appMode/appAddress';
import { isValidCheck } from '../appMode/appCheck';
import { getCurrentAppsDomain } from '../appMode/hostConfig';
import { requestAppSignIn } from '../services/appSignInApi';
import { getCurrentUser } from '../services/platformAuth';
import type { User } from '../types/identity';

/** Returns true when the return address is secure (or local) and is exactly this app's full address (live or preview) under the apps domain for this hostname. */
const isTrustedReturnUrl = (value: string, address: string) => {
  try {
    const { protocol, hostname } = new URL(value);
    const isLocal = hostname === 'localhost' || hostname.endsWith('.localhost');
    const isSecure = protocol === 'https:' || (protocol === 'http:' && isLocal);
    return isSecure && hostname === `${address}.${getCurrentAppsDomain()}`.toLowerCase();
  } catch {
    return false;
  }
};

/** Shows sign-in progress, then a placeholder for the app being opened. */
export default function HandoverPage() {
  const [user, setUser] = useState<User>();
  const [error, setError] = useState('');
  const params = new URLSearchParams(window.location.search);
  const app = params.get('app') ?? '';
  const check = params.get('check') ?? '';
  const hasAppsDomain = getCurrentAppsDomain() !== '';
  const canSignIn = hasAppsDomain && isValidAppAddress(app) && isValidCheck(check);

  useEffect(() => {
    const controller = new AbortController();
    getCurrentUser(controller.signal)
      .then(setUser)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error ? reason.message : 'Unable to confirm your Platform sign-in.'
          );
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!user || !canSignIn) {
      return undefined;
    }
    const controller = new AbortController();
    requestAppSignIn(app, controller.signal)
      .then(({ code, returnUrl }) => {
        if (!isTrustedReturnUrl(returnUrl, app)) {
          setError('The app returned an address that cannot be trusted.');
          return;
        }
        window.location.replace(
          `${returnUrl}?code=${encodeURIComponent(code)}&check=${encodeURIComponent(check)}`
        );
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(reason instanceof Error ? reason.message : 'Unable to open this app.');
      });
    return () => controller.abort();
  }, [user, canSignIn, app, check]);

  const renderBody = () => {
    if (error) {
      return <p className="text-sm text-destructive">{error}</p>;
    }
    if (!user) {
      return <LoadingState>Checking your sign-in…</LoadingState>;
    }
    if (!hasAppsDomain) {
      return (
        <p className="text-sm text-app-text-muted">
          Apps can&apos;t be opened from this address. Use the Fabrik address for your environment.
        </p>
      );
    }
    if (!isValidAppAddress(app)) {
      return <p className="text-sm text-app-text-muted">That address isn&apos;t a Fabrik app.</p>;
    }
    if (!isValidCheck(check)) {
      return (
        <p className="text-sm text-app-text-muted">
          This sign-in link is incomplete. Open the app address again.
        </p>
      );
    }
    const { first_name: firstName, last_name: lastName, email } = user;
    const name = [firstName, lastName].filter(Boolean).join(' ') || email;
    return (
      <>
        <LoadingState>Opening {app}…</LoadingState>
        {name && <p className="text-xs text-app-text-muted">Signed in as {name}</p>}
      </>
    );
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      {renderBody()}
    </main>
  );
}
